import {skipToken} from '@reduxjs/toolkit/query';
import {Alert} from 'platform/components';
import {match, Pattern} from 'ts-pattern';

import {isNil} from 'ramda';

import {useGetPkwEnvkvStatusQuery} from '@dms/api/vehicle';
import i18n from '@dms/i18n';

import {useTenant} from '../../hooks/useTenant';
import {getPkwEnvkvAdvertisingCorrectionIssueCodes} from '../../utils/getPkwEnvkvIssueLabels';
import {PkwEnvkvAdvertisingFeedback} from '../PkwEnvkvAdvertisingFeedback/PkwEnvkvAdvertisingFeedback';

export function PkwEnvkvAdvertisingNotice(props: {vehicleId: string}) {
  const {tenant} = useTenant();
  const isGermanTenant = tenant?.country === 'DEU';
  const {
    currentData: status,
    isFetching,
    isError,
    refetch,
  } = useGetPkwEnvkvStatusQuery(isGermanTenant ? {vehicleId: props.vehicleId} : skipToken, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
  });

  // A refresh of a known status (on window focus) keeps the notice on screen.
  return match({isGermanTenant, isFetching, isError, status})
    .with({isGermanTenant: false}, () => null)
    .when(
      (state) => state.isError || (state.isFetching && isNil(state.status)),
      () => (
        <Alert
          type="banner"
          variant={isError ? 'warning' : 'info'}
          title={i18n.t('page.pkwEnvkv.title')}
          message={i18n.t(
            isError ? 'page.pkwEnvkv.advertisingIssues.unknown' : 'general.labels.loading'
          )}
          hyperlinks={
            isError
              ? [
                  {
                    title: i18n.t('general.actions.retry'),
                    href: '#',
                    onClick: (event) => {
                      event.preventDefault();
                      void refetch();
                    },
                  },
                ]
              : []
          }
        />
      )
    )
    .with(
      {
        status: {
          supported: true,
          envkvRequired: true,
          advertisingStatus: Pattern.union('RED', 'YELLOW'),
        },
      },
      ({status: currentStatus}) => (
        <PkwEnvkvAdvertisingFeedback
          type="banner"
          vehicleId={props.vehicleId}
          variant={currentStatus.advertisingStatus === 'RED' ? 'blocked' : 'warning'}
          issueCodes={
            currentStatus.advertisingStatus === 'RED'
              ? getPkwEnvkvAdvertisingCorrectionIssueCodes(currentStatus)
              : currentStatus.issues
          }
          requirementReasonCodes={currentStatus.envkvRequiredReasonCodes}
        />
      )
    )
    .otherwise(() => null);
}
